import { Chip, Stack, Typography } from '@mui/material';
import { FC } from 'react';
import { VyPaper } from '../../../../../components/new-design/data-display/VyPaper/VyPaper';
import { ContractPageData } from '@vy/api-definition';
import { ContractStatus, Plan } from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useQueryClient } from 'react-query';
import { ContractStatusChip } from '../../../../../components/design/ContractStatusChip';
import { EditableTextLabel } from '../../../../../components/new-design/inputs/EditableLabel/EditableTextLabel';
import { useExtendContract } from '../../../../../hooks/requests/use-extend-contract';
import { useSoftUpdatePlan } from '../../../../../hooks/requests/use-update-soft-plan';
import { useUpdateContractMemo } from '../../../../../hooks/requests/use-update-contract-memo';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../utils/vy-mutation';
import { ContractUpliftBanners } from '../../ContractUpliftBanners/ContractUpliftBanners';
import { EditableContractDateField } from './EditableContractDateField';
import { ContractDateField } from './ContractDateField';

export type DetailItem = {
  label: string;
  value: string;
  editable?: boolean;
  fieldKey?: 'purchaseOrder' | 'notes';
};

type ContractDetailsProps = {
  contractName: string;
  status: ContractStatus;
  detailItems: DetailItem[];
  itemsChunkSize?: number;
  plan: Plan;
  contract: ContractPageData['contract'];
};

export const ContractDetails: FC<ContractDetailsProps> = ({
  contractName,
  status,
  detailItems,
  plan,
  contract,
}) => {
  const queryClient = useQueryClient();
  const softUpdatePlanMutation = useSoftUpdatePlan(plan.id);
  const extendContractMutation = useExtendContract(contract.id);
  const updateContractMemoMutation = useUpdateContractMemo();
  const isAutoRenewalEnabled = plan.billingData.autoRenewal;

  const handleContractNameChange = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value === contractName) {
      return;
    }

    await runMutation(
      softUpdatePlanMutation,
      { name: event.target.value },
      {
        pending: 'Updating contract...',
        success: 'Contract updated',
        error: 'Failed to update contract',
      },
    );
  };

  const handlePoNumberChange = async (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value === plan.purchaseOrder) {
      return;
    }

    await runMutation(
      softUpdatePlanMutation,
      { purchaseOrder: event.target.value },
      {
        pending: 'Updating contract...',
        success: 'Contract updated',
        error: 'Failed to update contract',
      },
    );
  };

  const handleMemoChange = async (event: React.FocusEvent<HTMLInputElement>) => {
    const currentNotes = contract.notes ?? '';
    const newNotes = event.target.value;

    if (newNotes === currentNotes) {
      return;
    }

    await runMutation(
      updateContractMemoMutation,
      { contractId: contract.id, notes: event.target.value },
      {
        pending: 'Updating memo...',
        success: 'Memo updated',
        error: 'Failed to update memo',
      },
    );
  };

  const handleEndDateChange = async (date: Date | null) => {
    if (isNullOrUndefined(date)) {
      return;
    }

    await runMutation(
      extendContractMutation,
      {
        endDate: date,
      },
      {
        pending: 'Updating contract end date...',
        success: 'Contract end date updated successfully',
        error: 'Failed to update contract end date',
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.contractPage(contract.id),
          });
        },
      },
    );
  };

  return (
    <VyPaper sx={{ height: '100%' }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2
        }}>
        <EditableTextLabel
          label={contractName}
          onSave={handleContractNameChange}
          variant="h6"
          sx={{ fontWeight: 600 }}
        />
        <Stack direction="row" spacing={1}>
          {isAutoRenewalEnabled && <Chip label="Auto renewal" size="medium" />}
          {(isNotNullOrUndefined(plan.trial) || contract.isTrial) && <Chip label="Trial" size="medium" />}
          <ContractStatusChip status={status} endDate={contract.endDate} />
        </Stack>
      </Stack>
      <Stack
        direction="row"
        spacing={4.25}
        sx={{
          height: "66px",
          gap: "34px"
        }}>
        <ContractDateField label="Start Date" value={contract.startDate} />

        <EditableContractDateField
          contractEndDate={contract.endDate ?? new Date()}
          label="End Date"
          value={contract.endDate}
          onSave={handleEndDateChange}
        />
      </Stack>
      <Stack
        direction="row"
        spacing={4.25}
        sx={{
          height: "66px",
          flexWrap: "wrap",
          gap: "34px",
          pt: 2
        }}>
        {detailItems.map((detail) => {
          const onSaveHandler =
            detail.fieldKey === 'notes' ? handleMemoChange : handlePoNumberChange;
          const memoInputProps = detail.fieldKey === 'notes' ? { maxLength: 500 } : undefined;

          return (
            <Stack key={detail.label} spacing="6px">
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(27, 61, 85, 0.6)',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '20px',
                }}
              >
                {detail.label}
              </Typography>

              {detail.editable ? (
                <EditableTextLabel
                  label={detail.value}
                  onSave={onSaveHandler}
                  variant="body2"
                  sx={{ fontWeight: 400 }}
                  inputProps={memoInputProps}
                />
              ) : (
                <Typography variant="body2" sx={{ paddingTop: '6px' }}>
                  {detail.value}
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
      <ContractUpliftBanners plan={plan} />
    </VyPaper>
  );
};
